import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Eye, ChevronRight } from "lucide-react";
import { api, catPath, formatDate, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ArticleCard } from "@/components/news/ArticleCard";
import { SectionTitle } from "@/components/news/SectionTitle";
import { CommentsSection } from "@/components/news/CommentsSection";
import { ShareBar, ShareIcons } from "@/components/news/ShareBar";

const ArticleMeta = ({ a, url }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-5 py-3 border-y border-zinc-200">
    <div className="meta text-sm">
      <span className="font-bold text-[#0F172A]">{a.author}</span><span>·</span>
      <span>{formatDate(a.published_at, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span><span>·</span>
      <span className="inline-flex items-center gap-1"><Eye size={13} /> {a.views}</span>
    </div>
    <ShareIcons title={a.title} url={url} />
  </div>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const { catName } = useSettings();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null);
    setErr(false);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="text-center py-20" data-testid="article-not-found"><h1 className="font-heading text-3xl font-black">Article introuvable</h1><Link to="/" className="text-red-600 font-bold mt-4 inline-block">Retour à l'accueil</Link></div>;
  if (!a) return <div className="h-96 bg-white/60 animate-pulse" />;

  const url = window.location.href;

  return (
    <div className="grid lg:grid-cols-3 gap-10">
      <article className="lg:col-span-2" data-testid="article-detail-content">
        <nav className="text-xs text-zinc-500 flex items-center gap-1 mb-4" data-testid="breadcrumb"><Link to="/" className="hover:text-red-600">Accueil</Link><ChevronRight size={12} /><Link to={catPath(a.category)} className="hover:text-red-600">{catName(a.category)}</Link></nav>
        <Link to={catPath(a.category)} className="badge-cat">{catName(a.category)}</Link>
        <h1 data-testid="article-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mt-3">{a.title}</h1>
        {a.excerpt && <p className="text-lg text-zinc-600 mt-4 leading-relaxed">{a.excerpt}</p>}
        <ArticleMeta a={a} url={url} />
        {a.image_url && <figure className="mt-6"><img src={mediaUrl(a.image_url)} alt={a.title} className="w-full aspect-[16/9] object-cover" /></figure>}
        <div className="prose-article mt-8" data-testid="article-body">{a.content.split(/\n\s*\n/).map((p, i) => <p key={`${a.id}-p${i}-${p.length}`}>{p}</p>)}</div>
        {a.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-6">{a.tags.map((t) => <span key={t} className="text-xs bg-white border px-2 py-1">#{t}</span>)}</div>}
        <ShareBar title={a.title} url={url} />
        <CommentsSection articleId={a.id} />
      </article>
      <aside>
        <SectionTitle to={catPath(a.category)}>À lire aussi</SectionTitle>
        <div className="grid gap-5" data-testid="related-articles">{a.related.map((r) => <ArticleCard key={r.id} article={r} />)}</div>
      </aside>
    </div>
  );
}
